import {
    type GenericTrackingEvent,
    type LinkInfo,
    type NavigationTrackingEvent,
    type TrackingEvent,
    TrackingEventLocation,
    TrackingEventName,
    TrackingEventType
} from "./types";
import {Nav} from "../common/nav";
import type {Maybe, Nullable} from "../bootstrap/common/types";
import {autoRegister, resolve} from "../container";
import {omitUndefined} from "../common/utils/objects";
import {DATA_TRACKING_EVENT_NAME, DATA_TRACKING_PAYLOAD_PREFIX} from "./trackingAttributes";

export type TrackingEventInfo = {
    text: string;
    type?: TrackingEventType;
    link?: string;
    location?: TrackingEventLocation;
    contentId?: string;
}

export interface TrackingEventFactory {
    createFromAttributes: (element: Element, event: Event) => TrackingEvent<any>;
    createFrom: (event: Event, eventName: TrackingEventName, eventInfo: TrackingEventInfo) => TrackingEvent<any>;
}

export class GenericTrackingEventFactory implements TrackingEventFactory {
    public constructor(private nav: Nav) {
    }

    public createFrom(event: Event, eventName: TrackingEventName, eventInfo: TrackingEventInfo): TrackingEvent<any> {
        return {
            name: eventName,
            data: omitUndefined({
                text: eventInfo.text,
                type: eventInfo.type || TrackingEventType.CLICK,
                link: this.linkInfo(eventInfo.link),
                contentId: eventInfo.contentId || this.makeContentId(event)
            })
        };
    }

    public createFromAttributes(element: Element, event: Event): GenericTrackingEvent {
        const eventName = element.getAttribute(DATA_TRACKING_EVENT_NAME) as TrackingEventName;
        if (!eventName || !Object.values(TrackingEventName).includes(eventName)) {
            throw new Error(`Missing or invalid tracking event name: ${eventName}`);
        }
        const text = this.getTrackingPayloadAttribute(element, "text");
        const type = this.getTrackingPayloadAttribute(element, "type") as TrackingEventType ?? TrackingEventType.CLICK;
        const linkInfo = this.linkInfo(this.getTrackingPayloadAttribute(element, "link"));
        if (!text) {
            throw new Error("Missing required tracking payload attribute: text");
        }
        return {
            name: eventName,
            data: omitUndefined({
                text: text,
                type: type,
                link: linkInfo,
                contentId: this.makeContentId(event)
            })
        };
    }

    protected makeContentId(event: Event): string {
        const composedPath = Array.from(event.composedPath());
        if (composedPath.isNotEmpty()) {
            return this.concatIds(composedPath.filter(element => element instanceof Element));
        }

        const evTarget = event.target;
        if (evTarget instanceof HTMLElement) {
            const parentChain = [evTarget].concat(evTarget.parents());
            return this.concatIds(parentChain);
        } else {
            return "";
        }
    }

    private concatIds(elements: Element[]): string {
        return elements
            .map(elm => elm.id)
            .filter(id => !!id)
            .reverse().join(".");
    }

    protected getTrackingPayloadAttribute(element: Element, key: string): Nullable<string> {
        return element.getAttribute(DATA_TRACKING_PAYLOAD_PREFIX + key);
    }

    protected linkInfo(targetUrl: Maybe<Nullable<string>>): Maybe<LinkInfo> {
        if (!targetUrl) {
            return undefined;
        }
        return {
            targetUrl: this.nav.absoluteHrefFrom(targetUrl),
            isExternal: !this.nav.isInternalLink(targetUrl)
        };
    }
}

export class NavigationTrackingEventFactory extends GenericTrackingEventFactory {
    public createFrom(event: Event, eventName: TrackingEventName, eventInfo: TrackingEventInfo): NavigationTrackingEvent {
        const baseEvent = super.createFrom(event, eventName, eventInfo);
        return {
            name: eventName,
            data: {
                ...baseEvent.data,
                location: eventInfo.location || this.locationOf(event)
            }
        };
    }

    public createFromAttributes(element: Element, event: Event): NavigationTrackingEvent {
        const trackingEvent = super.createFromAttributes(element, event) as NavigationTrackingEvent;
        let location = this.getTrackingPayloadAttribute(element, "location") as TrackingEventLocation;
        if (!location) {
            location = this.locationOf(event);
        } else if (!Object.values(TrackingEventLocation).includes(location)) {
            throw new Error(`Invalid tracking payload attribute for location: ${location}`);
        }
        trackingEvent.name = TrackingEventName.NAVIGATION_INTERACT;
        trackingEvent.data.location = location;
        return trackingEvent;
    }

    private locationOf(event: Event): TrackingEventLocation {
        const eventPath: Set<string> = new Set(Array.from(event.composedPath())
            .filter(element => element instanceof Element)
            .map(element => element.tagName));
        if (eventPath.has("HEADER")) {
            return TrackingEventLocation.HEADER;
        }
        if (eventPath.has("FOOTER")) {
            return TrackingEventLocation.FOOTER;
        }
        if (eventPath.has("MAIN")) {
            return TrackingEventLocation.MAIN;
        }
        return TrackingEventLocation.IN_SITE;
    }
}

@autoRegister()
export class TrackingEventFactories {
    private factories: Map<TrackingEventName, TrackingEventFactory>;

    public constructor(private nav: Nav = resolve(Nav)) {
        const genericTrackingEventFactory = new GenericTrackingEventFactory(this.nav);
        this.factories = new Map([
            [TrackingEventName.TEXTLINK_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.BUTTON_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.TEASER_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.ACCORDION_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.NAVIGATION_INTERACT, new NavigationTrackingEventFactory(this.nav)]
        ]);
    }

    public createFrom(event: Event, eventName: TrackingEventName, eventInfo: TrackingEventInfo): TrackingEvent<any> {
        const factory = this.factories.get(eventName);
        if (!factory) {
            throw new Error(`No factory found for tracking event name: ${eventName}`);
        }
        return factory.createFrom(event, eventName, eventInfo);
    }

    public createFromAttributes(event: Event): Nullable<TrackingEvent<any>> {
        const trackableElement = Array.from(event.composedPath())
            .filter(element => element instanceof Element)
            .filter(element => element.hasAttribute(DATA_TRACKING_EVENT_NAME))
            .filter(element => element.isVisible())
            .first();
        if (!trackableElement) {
            return null;
        }

        const name = trackableElement.getAttribute(DATA_TRACKING_EVENT_NAME) as TrackingEventName;
        const factory = this.factories.get(name);
        if (!factory) {
            throw new Error(`No factory found for tracking event name: ${name}`);
        }
        return factory.createFromAttributes(trackableElement, event);
    }
}